<template>
  <span
    v-if="!active"
    class="sc-edit sc-edit--date"
    :class="{ 'is-disabled': disabled }"
    role="button"
    :tabindex="disabled ? -1 : 0"
    @click="activate"
    @keydown.enter.prevent="activate"
  >{{ text }}</span>
  <span v-else class="sc-edit-group" @focusout="onFocusOut">
    <input
      ref="yearEl"
      v-model="dy"
      class="sc-edit-input sc-edit-input--y"
      inputmode="numeric"
      aria-label="년"
      @keydown.enter.prevent="commit"
      @keydown.esc.prevent="cancel"
    />년
    <input
      v-model="dm"
      class="sc-edit-input sc-edit-input--m"
      inputmode="numeric"
      aria-label="월"
      @keydown.enter.prevent="commit"
      @keydown.esc.prevent="cancel"
    />월
    <template v-if="type === 'ymd'">
      <input
        v-model="dd"
        class="sc-edit-input sc-edit-input--d"
        inputmode="numeric"
        aria-label="일"
        @keydown.enter.prevent="commit"
        @keydown.esc.prevent="cancel"
      />일
    </template>
  </span>
</template>

<script setup lang="ts">
import { computed, nextTick, ref } from 'vue'

/* 계약서 날짜 — 평소에는 「　　년　　월　　일」 텍스트, 누르면 년·월(·일) 입력 */
const props = withDefaults(defineProps<{
  y: string
  m: string
  d?: string
  type?: 'ym' | 'ymd'
  disabled?: boolean
}>(), { d: '', type: 'ymd', disabled: false })

const emit = defineEmits<{
  'update:y': [value: string]
  'update:m': [value: string]
  'update:d': [value: string]
}>()

const active = ref(false)
const dy = ref('')
const dm = ref('')
const dd = ref('')
const yearEl = ref<HTMLInputElement | null>(null)

const text = computed(() => {
  const y = String(props.y || '').trim()
  const m = String(props.m || '').trim()
  const d = String(props.d || '').trim()
  const bw = (v: string, n: number) => (v || '　'.repeat(Math.min(n, 8)))
  if (props.type === 'ym') {
    if (!y && !m) return '　　년　　월'
    return `${bw(y, 4)}년 ${bw(m, 2)}월`
  }
  if (!y && !m && !d) return '　　년　　월　　일'
  return `${bw(y, 4)}년 ${bw(m, 2)}월 ${bw(d, 2)}일`
})

function activate() {
  if (props.disabled) return
  dy.value = String(props.y || '')
  dm.value = String(props.m || '')
  dd.value = String(props.d || '')
  active.value = true
  nextTick(() => yearEl.value?.focus())
}

function commit() {
  if (!active.value) return
  active.value = false
  const y = dy.value.trim()
  const m = dm.value.trim()
  const d = dd.value.trim()
  if (y !== String(props.y || '')) emit('update:y', y)
  if (m !== String(props.m || '')) emit('update:m', m)
  if (props.type === 'ymd' && d !== String(props.d || '')) emit('update:d', d)
}

function cancel() {
  active.value = false
}

function onFocusOut(e: FocusEvent) {
  const next = e.relatedTarget as Node | null
  if (next && (e.currentTarget as HTMLElement).contains(next)) return
  commit()
}
</script>
